import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IPurchaseContractDetailResponse } from '../../../model/base/response/purchase-contract-response';

interface PurchaseContractDetailState {
  contract: IPurchaseContractDetailResponse | null;
  loading: boolean;
  error: string | null;
}

type PurchaseContractDetailAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: IPurchaseContractDetailResponse }
  | { type: 'LOAD_ERROR'; payload: string };

const initialState: PurchaseContractDetailState = {
  contract: null,
  loading: false,
  error: null,
};

const PurchaseContractDetailStateContext = createContext<PurchaseContractDetailState | undefined>(undefined);
const PurchaseContractDetailDispatchContext = createContext<React.Dispatch<PurchaseContractDetailAction> | undefined>(undefined);

function purchaseContractDetailReducer(state: PurchaseContractDetailState, action: PurchaseContractDetailAction): PurchaseContractDetailState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, contract: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function PurchaseContractDetailProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(purchaseContractDetailReducer, initialState);

  return (
    <PurchaseContractDetailStateContext.Provider value={state}>
      <PurchaseContractDetailDispatchContext.Provider value={dispatch}>
        {children}
      </PurchaseContractDetailDispatchContext.Provider>
    </PurchaseContractDetailStateContext.Provider>
  );
}

export function usePurchaseContractDetailState() {
  const context = useContext(PurchaseContractDetailStateContext);
  if (context === undefined) {
    throw new Error('usePurchaseContractDetailState must be used within a PurchaseContractDetailProvider');
  }
  return context;
}

export function usePurchaseContractDetailDispatch() {
  const context = useContext(PurchaseContractDetailDispatchContext);
  if (context === undefined) {
    throw new Error('usePurchaseContractDetailDispatch must be used within a PurchaseContractDetailProvider');
  }
  return context;
}
